import { useEffect, useState, useRef } from 'react';
import { useIsMobile } from '@hooks/use-mobile';

export const useRowWiseCardHeight = () => {
  const [rowHeights, setRowHeights] = useState<Map<number, number>>(new Map());
  const isMobile = useIsMobile();
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const calculateRowHeights = () => {
      const contentHeaders = document.querySelectorAll('[data-content-header]');
      
      if (contentHeaders.length === 0) {
        return;
      }

      const cardsGrid = document.querySelector('[data-cards-grid]');
      if (!cardsGrid) {
        return;
      }

      const containerWidth = cardsGrid.getBoundingClientRect().width;
      
      const rowGroups = new Map<number, HTMLElement[]>();
      const tolerance = 3;
      
      contentHeaders.forEach((header) => {
        const rect = header.getBoundingClientRect();
        const containerRect = cardsGrid.getBoundingClientRect();
        
        const relativeTop = rect.top - containerRect.top;
        const rowIndex = Math.floor(relativeTop / tolerance) * tolerance;
        
        if (!rowGroups.has(rowIndex)) {
          rowGroups.set(rowIndex, []);
        }
        rowGroups.get(rowIndex)!.push(header as HTMLElement);
      });

      const consolidatedRows = new Map<number, HTMLElement[]>();
      let currentRowIndex = 0;
      
      const sortedRowIndexes = Array.from(rowGroups.keys()).sort((a, b) => a - b);
      
      for (let i = 0; i < sortedRowIndexes.length; i++) {
        const rowIndex = sortedRowIndexes[i];
        const elements = rowGroups.get(rowIndex)!;
        
        if (i === 0) {
          consolidatedRows.set(currentRowIndex, elements);
        } else {
          const prevRowIndex = sortedRowIndexes[i - 1];
          const heightDiff = Math.abs(rowIndex - prevRowIndex);
          
          if (heightDiff < 50) {
            const existingElements = consolidatedRows.get(currentRowIndex) || [];
            consolidatedRows.set(currentRowIndex, [...existingElements, ...elements]);
          } else {
            currentRowIndex++;
            consolidatedRows.set(currentRowIndex, elements);
          }
        }
      }

      const rowHeightMap = new Map<number, number>();
      consolidatedRows.forEach((elements, rowIndex) => {
        let maxHeight = 0;
        elements.forEach((element) => {
          const height = element.getBoundingClientRect().height;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        rowHeightMap.set(rowIndex, maxHeight);
      });

      consolidatedRows.forEach((elements, rowIndex) => {
        elements.forEach((element) => {
          element.setAttribute('data-row-index', rowIndex.toString());
        });
      });

      setRowHeights(rowHeightMap);
    };

    const timeoutId = setTimeout(calculateRowHeights, 100);

    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(calculateRowHeights, 100);
    };

    if (window.ResizeObserver) {
      observerRef.current = new ResizeObserver(() => {
        setTimeout(calculateRowHeights, 50);
      });

      const contentHeaders = document.querySelectorAll('[data-content-header]');
      contentHeaders.forEach((header) => {
        observerRef.current?.observe(header);
      });
    }

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isMobile]);

  const getRowHeight = (element: HTMLElement | null): number => {
    if (!element || isMobile) {
      return 0;
    }

    const rowIndex = parseInt(element.getAttribute('data-row-index') || '0');
    return rowHeights.get(rowIndex) || 0;
  };

  const recalculateHeights = () => {
    const contentHeaders = document.querySelectorAll('[data-content-header]');
    
    if (contentHeaders.length === 0) {
      return;
    }

    const cardsGrid = document.querySelector('[data-cards-grid]');
    if (!cardsGrid) {
      return;
    }

    const containerWidth = cardsGrid.getBoundingClientRect().width;
    
    const rowGroups = new Map<number, HTMLElement[]>();
    const tolerance = 3;
    
    contentHeaders.forEach((header) => {
      const rect = header.getBoundingClientRect();
      const containerRect = cardsGrid.getBoundingClientRect();
      
      const relativeTop = rect.top - containerRect.top;
      const rowIndex = Math.floor(relativeTop / tolerance) * tolerance;
      
      if (!rowGroups.has(rowIndex)) {
        rowGroups.set(rowIndex, []);
      }
      rowGroups.get(rowIndex)!.push(header as HTMLElement);
    });

    const consolidatedRows = new Map<number, HTMLElement[]>();
    let currentRowIndex = 0;
    
    const sortedRowIndexes = Array.from(rowGroups.keys()).sort((a, b) => a - b);
    
    for (let i = 0; i < sortedRowIndexes.length; i++) {
      const rowIndex = sortedRowIndexes[i];
      const elements = rowGroups.get(rowIndex)!;
      
      if (i === 0) {
        consolidatedRows.set(currentRowIndex, elements);
      } else {
        const prevRowIndex = sortedRowIndexes[i - 1];
        const heightDiff = Math.abs(rowIndex - prevRowIndex);
        
        if (heightDiff < 50) {
          const existingElements = consolidatedRows.get(currentRowIndex) || [];
          consolidatedRows.set(currentRowIndex, [...existingElements, ...elements]);
        } else {
          currentRowIndex++;
          consolidatedRows.set(currentRowIndex, elements);
        }
      }
    }

    const rowHeightMap = new Map<number, number>();
    consolidatedRows.forEach((elements, rowIndex) => {
      let maxHeight = 0;
      elements.forEach((element) => {
        const height = element.getBoundingClientRect().height;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });
      rowHeightMap.set(rowIndex, maxHeight);
    });

    consolidatedRows.forEach((elements, rowIndex) => {
      elements.forEach((element) => {
        element.setAttribute('data-row-index', rowIndex.toString());
      });
    });

    setRowHeights(rowHeightMap);
  };

  return { getRowHeight, isMobile, recalculateHeights };
}; 